import { useRef, useState, useEffect, useMemo } from "react";
import { CompanyInterface } from "../../../model/HeaderDashboardStore.model";
import { useHeaderDashboardActions } from "../../../lib/useHeaderDashboardActions";
import { Option } from '@/shared/components/common/ComboInputSelect/ComboInputSelect';
import { UserInterface } from "@/shared/model/api.types";
import { parseUsersInDepartment } from "@/shared/lib/users";
import { useWorkspace } from "@/features/Workspace/lib/useWorkspace";
import { useNotificationActions } from "@/features/Notification";
import { useHeaderDashboard } from "../../../lib/useHeaderDashboard";
import { usePlan } from "@/entities/Plan";
import { CreateCompanysPlanForm, CompanyTarget } from './CreateCompanysPlanPopup.model';
import { usePlanActions } from "@/entities/Plan/lib/usePlanActions";
import { useAuth } from '@/features/Auth/lib/useAuth';
import { isSalesSystemAccount, salesAdministrationDepartments } from '@/features/Auth/lib/salesAccess';

const useCreateCompanysPlanPopup = (onClose: () => void, initialData?: Partial<CreateCompanysPlanForm>) => {
  const [dropdownValue, setDropdownValue] = useState<Option | Option[]>();


  const initForm = useMemo((): CreateCompanysPlanForm => {
    if (initialData) {
      if (initialData.multy) {
        setDropdownValue(
          initialData.multy.map(item => ({
            value: item.object,
            label: item.object
          }))
        );
      }

      setSelectedCompanies(
        (initialData.multy ?? []).map(item => ({
          inn: item.object,
          label: item.object // или используйте подходящий label, если есть
        }))
      );
      return {
        plan_type_id: 0,
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        target_value: 0,
        user_id: 0,
        department_id: 0,
        multy: [],
        ...initialData,
      };
    }
    return {
      plan_type_id: 0,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      target_value: 0,
      user_id: 0,
      department_id: 0,
      multy: [] as CompanyTarget[],
    }
  }, [initialData]);

  const { createMyltyPlan } = usePlanActions()
  const { fetchCompanies } = useHeaderDashboardActions();
  const [inputValue, setInputValue] = useState<string | number>('');
  const [options, setOptions] = useState<Option[]>([]);
  const { loading: dashboardLoading, error: dashboardError } = useHeaderDashboard();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<UserInterface[]>([]);
  const { users, departments, subGenDepartments, loading: workspaceLoading, error: workspaceError } = useWorkspace();
  const { user } = useAuth();
  const availableDepartments = useMemo(
    () => isSalesSystemAccount(user) ? salesAdministrationDepartments(departments) : subGenDepartments,
    [departments, subGenDepartments, user]
  );
  const { topAlert } = useNotificationActions();  
  const { planTypes } = usePlan();
  const filteredPlanTypes = useMemo(() => planTypes.filter(pt => pt.code.includes('EMPLOYEE_MONTH') && !pt.code.includes('MULTY')), [planTypes]);
  const visibleMonth = true;
  const [form, setForm] = useState<CreateCompanysPlanForm>(initForm);
  const [selectedCompanies, setSelectedCompanies] = useState<{inn: string, label: string}[]>([]);

  useEffect(() => {
    const usersResult = parseUsersInDepartment(
      users,
      form && "department_id" in form ? (form.department_id as number) : null
    );
    setFilteredUsers(prev => {
      const isSame = Array.isArray(usersResult) && Array.isArray(prev) && usersResult.length === prev.length && usersResult.every((v, i) => v.id === prev[i].id);
      return isSame ? prev : (usersResult ?? []);
    });
  }, [users, form.department_id, form]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputValue || (typeof inputValue === 'string' && !inputValue.trim())) {
      setOptions([
        { value: 'vacant', label: 'Вакант' }
      ]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const res = await fetchCompanies(inputValue.toString());
      if (Array.isArray(res)) {
        setOptions([
          { value: 'vacant', label: 'Вакант' },
          ...res.map((company: CompanyInterface) => ({
            value: company.inn.toString(),
            label: company.name,
          }))
        ]);
      } else {
        setOptions([
          { value: 'vacant', label: 'Вакант' }
        ]);
      }
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    let arr: Option[] = [];

    if (Array.isArray(dropdownValue)) arr = dropdownValue;
    else if (dropdownValue) arr = [dropdownValue];
    setForm((prev) => ({
      ...prev,
      multy: arr.map((item) => {
        const exist = prev.multy.find((m) => String(m.object) === String(item.value));
        return {
          object: String(item.value),
          target_values: filteredPlanTypes.map(pt => {
            const existVal = exist?.target_values.find(tv => tv.plan_type_id === pt.id);
            return { plan_type_id: pt.id, target_value: existVal ? existVal.target_value : 0 };
          })
        };
      })
    }));
    setSelectedCompanies((prev) => {
      const newCompanies = arr
        .filter(item => !prev.some(c => c.inn === String(item.value)))
        .map(item => ({ inn: String(item.value), label: item.label }));
      const filtered = prev.filter(c => arr.some(item => String(item.value) === c.inn));
      return [...filtered, ...newCompanies];
    });
  }, [dropdownValue, filteredPlanTypes]);

  useEffect(() => {
    if (availableDepartments.length > 0) {
      setForm(prev => {
        if (prev.department_id === availableDepartments[0].id) return prev;
        return {
          ...prev,
          department_id: availableDepartments[0].id
        };
      });
    }
  }, [availableDepartments]);

  const handleChangeTargetValue = (inn: string, plan_type_id: number, value: number) => {
    setForm((prev) => ({
      ...prev,
      multy: prev.multy.map((m) => m.object === inn ? {
        ...m,
        target_values: m.target_values.map(tv => tv.plan_type_id === plan_type_id ? { ...tv, target_value: value } : tv)
      } : m)
    }));
  };

  const handleRemoveFromMulty = (inn: string) => {
    setForm((prev) => ({
      ...prev,
      multy: prev.multy.filter((m) => m.object !== inn),
    }));
    setDropdownValue((prev) => {
      if (Array.isArray(prev)) {
        return prev.filter((opt) => String(opt.value) !== String(inn));
      }
      if (prev && String((prev as Option).value) === String(inn)) {
        return [];
      }
      return prev;
    });
    setSelectedCompanies((prev) => prev.filter(c => c.inn !== inn));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Фильтрация multy и target_values
    const filteredMulty = (form.multy || []).map(company => ({
      ...company,
      target_values: (company.target_values || []).filter(tv => tv.target_value > 0)
    })).filter(company => company.target_values.length > 0);

    if (!filteredMulty || filteredMulty.length === 0) {
      topAlert("Выберите хотя бы одну компанию с заполненными значениями плана", "warning", 1000);
      return;
    }
    if (
      !form.year || form.year === 0 ||
      !form.month || form.month === 0 ||
      !form.department_id || form.department_id === 0 ||
      !form.user_id || form.user_id === 0
    ) {
      topAlert("Пожалуйста, заполните все обязательные поля", "warning", 1000);
      return;
    }
    form.plan_type_id = planTypes.find(p => p.code === "MULTY_COMPANY_EMPLOYEE_MONTH")?.id ?? 0;
    if (form.plan_type_id === 0) {
      topAlert("Ошибка, не найдет тип плана по коду MULTY_COMPANY_EMPLOYEE_MONTH", "error", 1000);
      return;
    }
    // Используем отфильтрованный multy
    const formToSave = { ...form, multy: filteredMulty };
    const res = await createMyltyPlan(formToSave)
    if (res) {
      onClose();
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => {
      if (!prev) return prev;
      const newForm = {
        ...prev,
        [name]:
          [
            "target_value",
            "year",
            "month",
            "plan_type_id",
            "user_id",
            "department_id",
          ].includes(name)
            ? Number(value)
            : value,
      };
      if (name === "user_id") {
        newForm.plan_type_id = 0;
      }
      return newForm;
    });
  };

  return {
    inputValue,
    users: filteredUsers,
    departments: availableDepartments,
    setInputValue: (val: string | number) => setInputValue(val),
    options,
    dropdownValue,
    setDropdownValue: (val: Option | Option[]) => setDropdownValue(val),
    handleSave,
    loading: {
      users: workspaceLoading.users,
      departments: workspaceLoading.departments,
      companies: dashboardLoading.companies,
    },
    error: {
      users: workspaceError.users,
      departments: workspaceError.departments,
      companies: dashboardError.companies,
    },
    form,
    handleChange,
    handleChangeTargetValue,
    handleRemoveFromMulty,
    selectedCompanies,
    planTypes: filteredPlanTypes,
    visibleMonth,
  };
};

export default useCreateCompanysPlanPopup;
